import React, { useState, useEffect } from 'react';
import { Mail, HelpCircle, Headphones, Briefcase, MapPin, Phone, Clock, Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';
import { getSiteSettings } from '../utils/api';

const Contact = () => {
  const [settings, setSettings] = useState({
    office_address: '',
    office_hours: '',
    office_phone: '',
    office_email: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    twitter_url: '',
    linkedin_url: '',
    youtube_url: ''
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await getSiteSettings();
        if (res.data) {
          setSettings({
            office_address: res.data.office_address || '',
            office_hours: res.data.office_hours || '',
            office_phone: res.data.office_phone || '',
            office_email: res.data.office_email || '',
            facebook_url: res.data.facebook_url || '',
            instagram_url: res.data.instagram_url || '',
            tiktok_url: res.data.tiktok_url || '',
            twitter_url: res.data.twitter_url || '',
            linkedin_url: res.data.linkedin_url || '',
            youtube_url: res.data.youtube_url || ''
          });
        }
      } catch (error) {
        console.error('Failed to load site settings:', error);
      }
    };
    
    loadSettings();
  }, []);

  const hasOfficeInfo = settings.office_address || settings.office_phone || settings.office_hours;
  const hasSocialLinks = settings.facebook_url || settings.instagram_url || settings.twitter_url || settings.linkedin_url || settings.youtube_url;

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We're here to help. Reach out to us for questions, comments, technical support, or partnership opportunities.
          </p>
        </div>
      </div>

      {/* Contact Options */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {/* General Inquiries */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 bg-[#8B1818] rounded-full">
                    <Mail className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
                      General Inquiries
                    </h2>
                    <p className="text-slate-600 text-sm mt-1">
                      For questions, comments, feedback, or general inquiries about <span className="text-[#8B1818] font-semibold">Lex</span>DoRight products and services.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-lg px-5 py-3 border border-slate-200 md:ml-auto">
                  <p className="text-xs text-slate-500 mb-1">Email us at:</p>
                  <a 
                    href="mailto:admin@lexdoright.com" 
                    className="text-lg font-semibold text-[#8B1818] hover:text-[#701313] transition-colors"
                  >
                    admin@lexdoright.com
                  </a>
                </div>
              </div>
            </div>

            {/* Technical Support */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 bg-[#8B1818] rounded-full">
                    <Headphones className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
                      Technical Support
                    </h2>
                    <p className="text-slate-600 text-sm mt-1">
                      Need help with technical issues, account access, or platform functionality? Our support team is here to assist.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-lg px-5 py-3 border border-slate-200 md:ml-auto">
                  <p className="text-xs text-slate-500 mb-1">Email us at:</p>
                  <a 
                    href="mailto:support@lexdoright.com" 
                    className="text-lg font-semibold text-[#8B1818] hover:text-[#701313] transition-colors"
                  >
                    support@lexdoright.com
                  </a>
                </div>
              </div>
            </div>

            {/* Partners */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 bg-[#8B1818] rounded-full">
                    <Briefcase className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
                      Partnerships
                    </h2>
                    <p className="text-slate-600 text-sm mt-1">
                      Interested in partnering with us? We welcome collaborations with individuals, organizations, and other entities that share our vision of making legal resources and services more accessible. Likewise, we look forward to working together on innovative legal tech initiatives.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-lg px-5 py-3 border border-slate-200 md:ml-auto">
                  <p className="text-xs text-slate-500 mb-1">Email us at:</p>
                  <a 
                    href="mailto:partners@lexdoright.com" 
                    className="text-lg font-semibold text-[#8B1818] hover:text-[#701313] transition-colors"
                  >
                    partners@lexdoright.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Office Details & Social Media Section */}
          {(hasOfficeInfo || hasSocialLinks) && (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Office Details */}
              {hasOfficeInfo && (
                <div className="bg-gradient-to-br from-[#8B1818] to-[#701313] rounded-2xl p-8 text-white">
                  <h3 className="text-xl font-bold mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
                    Office Details
                  </h3>
                  <div className="space-y-4">
                    {settings.office_address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5 text-white/80" />
                        <div>
                          <p className="text-sm text-white/60 mb-1">Address</p>
                          <p className="text-white">{settings.office_address}</p>
                        </div>
                      </div>
                    )}
                    {settings.office_phone && (
                      <div className="flex items-start gap-3">
                        <Phone className="h-5 w-5 flex-shrink-0 mt-0.5 text-white/80" />
                        <div>
                          <p className="text-sm text-white/60 mb-1">Phone</p>
                          <a href={`tel:${settings.office_phone}`} className="text-white hover:text-white/80 transition-colors">
                            {settings.office_phone}
                          </a>
                        </div>
                      </div>
                    )}
                    {settings.office_email && (
                      <div className="flex items-start gap-3">
                        <Mail className="h-5 w-5 flex-shrink-0 mt-0.5 text-white/80" />
                        <div>
                          <p className="text-sm text-white/60 mb-1">Email</p>
                          <a href={`mailto:${settings.office_email}`} className="text-white hover:text-white/80 transition-colors">
                            {settings.office_email}
                          </a>
                        </div>
                      </div>
                    )}
                    {settings.office_hours && (
                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 flex-shrink-0 mt-0.5 text-white/80" />
                        <div>
                          <p className="text-sm text-white/60 mb-1">Office Hours</p>
                          <p className="text-white">{settings.office_hours}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Social Media */}
              {hasSocialLinks && (
                <div className="bg-slate-100 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
                    Follow Us
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Stay connected with us on social media for updates, tips, and legal insights.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    {settings.facebook_url && (
                      <a 
                        href={settings.facebook_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-[#8B1818] hover:text-[#8B1818] transition-colors"
                      >
                        <Facebook className="h-5 w-5" />
                        <span className="text-sm font-medium">Facebook</span>
                      </a>
                    )}
                    {settings.instagram_url && (
                      <a 
                        href={settings.instagram_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-[#8B1818] hover:text-[#8B1818] transition-colors"
                      >
                        <Instagram className="h-5 w-5" />
                        <span className="text-sm font-medium">Instagram</span>
                      </a>
                    )}
                    {settings.twitter_url && (
                      <a 
                        href={settings.twitter_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-[#8B1818] hover:text-[#8B1818] transition-colors"
                      >
                        <Twitter className="h-5 w-5" />
                        <span className="text-sm font-medium">Twitter/X</span>
                      </a>
                    )}
                    {settings.linkedin_url && (
                      <a 
                        href={settings.linkedin_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-[#8B1818] hover:text-[#8B1818] transition-colors"
                      >
                        <Linkedin className="h-5 w-5" />
                        <span className="text-sm font-medium">LinkedIn</span>
                      </a>
                    )}
                    {settings.youtube_url && (
                      <a 
                        href={settings.youtube_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-[#8B1818] hover:text-[#8B1818] transition-colors"
                      >
                        <Youtube className="h-5 w-5" />
                        <span className="text-sm font-medium">YouTube</span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Additional Info */}
          <div className="mt-16 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 text-center border border-slate-200">
            <HelpCircle className="h-12 w-12 text-[#8B1818] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Response Time</h3>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We strive to respond to all inquiries within 24-48 hours during business days. For urgent technical issues, please mark your email subject line with "URGENT" for priority handling.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
